import React, { useState, useEffect, useMemo } from 'react';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { getDatabase, ref, get, set, push } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import PaymentReceipt from './PaymentReceipt'; // Import PaymentReceipt

const PaySchoolFees = () => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [assignedClass, setAssignedClass] = useState('');
  const [studentType, setStudentType] = useState('returning');
  const [selectedTerm, setSelectedTerm] = useState('FIRST TERM');
  const [selectedSession, setSelectedSession] = useState(
    `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`
  );
  const [amount, setAmount] = useState(0);
  const [schoolFees, setSchoolFees] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [feeError, setFeeError] = useState('');
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const sessions = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const year = new Date().getFullYear() - i;
      return `${year}/${year + 1}`;
    });
  }, []);

  const terms = useMemo(() => ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'], []);
  const db = getDatabase();

  useEffect(() => {
    const schoolInfoRef = ref(db, 'schoolInfo/schoolFees');
    get(schoolInfoRef).then((snapshot) => {
        if (snapshot.exists()) setSchoolFees(snapshot.val());
        else console.log("No school fees data available");
    }).catch(console.error);

    if (currentUser) {
      const userRef = ref(db, `users/${currentUser.uid}`);
      get(userRef).then(userSnap => {
        const userData = userSnap.val();
        if (userData.role === 'parent' && userData.children) {
            const studentPromises = Object.keys(userData.children).map(studentId =>
                get(ref(db, `users/${studentId}`))
            );
            Promise.all(studentPromises).then(studentSnaps => {
                const studentList = studentSnaps.map(snap => ({ ...snap.val(), uid: snap.key }));
                setStudents(studentList);
                if (studentList.length > 0) {
                  setSelectedStudent(studentList[0]);
                }
            });
        } else {
            const studentData = { ...userData, uid: currentUser.uid };
            setStudents([studentData]);
            setSelectedStudent(studentData);
        }
      });
    }
  }, [currentUser, db]);

  useEffect(() => {
    if (selectedStudent) {
      setName(selectedStudent.fullName || '');
      setEmail(selectedStudent.email || '');
      setPhone(selectedStudent.phone || '');
      const userClass = selectedStudent.className || selectedStudent.class || selectedStudent.studentClass || '';
      setAssignedClass(userClass);
    }
  }, [selectedStudent]);

  useEffect(() => {
    setFeeError('');
    if (schoolFees && assignedClass) {
      const normalizedAssignedClass = assignedClass.replace(/\s+/g, '').toUpperCase();
      const feeKeys = Object.keys(schoolFees);
      const matchedKey = feeKeys.find(key => key.replace(/\s+/g, '').toUpperCase() === normalizedAssignedClass);

      if (!matchedKey) {
        setFeeError(`Fee information for class '${assignedClass}' could not be found. Please contact administration.`);
        setAmount(0);
        return;
      }

      const feeData = schoolFees[matchedKey];
      const fee = feeData[studentType];
      
      if (fee === undefined || fee === null) {
        setFeeError(`Fee for a '${studentType}' student in '${assignedClass}' is not set. Please contact administration.`);
        setAmount(0);
        return;
      }

      const numericFee = parseInt(String(fee).replace(/[^0-9]/g, ''), 10);
      setAmount(numericFee);

    } else if (schoolFees && !assignedClass) {
      setFeeError('This student is not assigned to a class. Please contact administration.');
      setAmount(0);
    }
  }, [schoolFees, assignedClass, studentType]);

  const config = {
    public_key: 'FLWPUBK-de141ad267bc824bb5d40b9afd5f6b23-X',
    tx_ref: Date.now(),
    amount,
    currency: 'NGN',
    payment_options: 'card,mobilemoney,ussd',
    customer: {
      email,
      phone_number: phone,
      name,
    },
    customizations: {
      title: 'Holy See Bright Academy School Fees',
      description: `Payment for ${selectedTerm} ${selectedSession}`,
      logo: 'https://holyseebright.com/logo.png',
    },
  };

  const handleFlutterwavePayment = useFlutterwave(config);

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    const student = students.find(s => s.uid === studentId);
    setSelectedStudent(student);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (feeError) {
        alert(`Please resolve the following error before proceeding: ${feeError}`);
        return;
    }
    if (!amount || amount <= 0) {
        alert("The payment amount is invalid. Please check the student details or contact administration.");
        return;
    }
    handleFlutterwavePayment({
      callback: (response) => {
         if (response.status === 'successful') {
            const studentId = selectedStudent.uid;
            const paymentData = {
                studentId,
                name: selectedStudent.fullName,
                email,
                phone,
                class: assignedClass,
                session: selectedSession,
                term: selectedTerm,
                amount,
                status: 'successful',
                transactionRef: response.tx_ref,
                date: new Date().toISOString(),
            };
            const newPaymentKey = push(ref(db, 'payments')).key;
            set(ref(db, `payments/${newPaymentKey}`), paymentData);
            set(ref(db, `users/${studentId}/fees/${selectedSession}/${selectedTerm}`), 'cleared');
            setReceiptData(paymentData);
            setPaymentSuccessful(true);
          } else {
            alert('Payment failed!');
          }
         closePaymentModal();
      },
      onClose: () => {},
    });
  }

  if (paymentSuccessful && receiptData) {
    return <PaymentReceipt payment={receiptData} />;
  }

  return (
    <div className="container mt-4">
      <h2>Pay School Fees</h2>
      <form onSubmit={handleSubmit} className="card p-4">
        {students.length > 1 && (
          <div className="form-group">
            <label>Select Student:</label>
            <select className="form-control" onChange={handleStudentChange} value={selectedStudent?.uid || ''}>
              {students.map(s => <option key={s.uid} value={s.uid}>{s.fullName}</option>)}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Name:</label>
          <input type="text" className="form-control" value={name} readOnly disabled />
        </div>
        <div className="form-group">
          <label>Email:</label>
          <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Phone:</label>
          <input type="tel" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
        <div className="form-row">
            <div className="form-group col-md-6">
                <label>Session:</label>
                <select className="form-control" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} required>
                    {sessions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div className="form-group col-md-6">
                <label>Term:</label>
                <select className="form-control" value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)} required>
                    {terms.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
        </div>
         <div className="form-row">
            <div className="form-group col-md-6">
                <label>Class:</label>
                <input type="text" className="form-control" value={assignedClass} readOnly disabled />
            </div>
            <div className="form-group col-md-6">
                <label>Student Type:</label>
                <select className="form-control" value={studentType} onChange={(e) => setStudentType(e.target.value)} required>
                    <option value="new">New Student</option>
                    <option value="returning">Returning Student</option>
                </select>
            </div>
        </div>
        
        {feeError && <div className="alert alert-danger">{feeError}</div>}

        <div className="mt-3">
            <h4>Amount: ₦{amount.toLocaleString()}</h4>
        </div>
        <button type="submit" className="btn btn-primary mt-3" disabled={!!feeError || amount <= 0}>
          Pay Now
        </button>
      </form>
    </div>
  );
};

export default PaySchoolFees;
